import { useCallback } from 'react';
import { useCardPage } from '../contexts/CardPageContext';
import { useCardEdit } from '../contexts/CardEditContext';

export type WorkflowStep = 'UPDATE' | 'REVIEW' | 'NAVER';

export interface WorkflowState {
  updateDone:       boolean;
  pendingDropCount: number;
  naverDone:        boolean;
  priceUpdating:    boolean;
  naverSyncing:     boolean;
  /** 타 샵 시세 대조 분류(CLASSIFY_DROPS) 잡이 진행 중인가. */
  classifying:      boolean;
  selectedCount:    number;
  totalCount:       number;
  /** 서버는 오늘 받기 완료인데 하락 검토 목록을 받지 못했다 — ③ 을 막는다(감사 card-controltower#656). */
  reviewListMissing?: boolean;
}

type NaverGateInput = Pick<WorkflowState, 'selectedCount' | 'updateDone' | 'pendingDropCount' | 'reviewListMissing'>;

export const REVIEW_LIST_MISSING_REASON = '하락 검토 목록을 받지 못했습니다 — 다시 시도해 주세요';

/**
 * Naver 반영 차단 사유 — "오늘의 작업"(DailyWorkflowBar의 ③ 스토어 반영 스텝) 전용.
 * 이 스텝은 항상 전체 카드를 대상으로 반영하므로, 오늘 전체 조회(updateDone)가 끝났고
 * 대기 중인 하락 확인이 하나도 없어야만(pendingDropCount === 0, 매장 전체 기준) 열린다.
 */
export const naverBlockReason = (s: NaverGateInput): string | undefined => {
  if (s.selectedCount === 0)  return '반영할 카드를 선택해주세요';
  if (s.reviewListMissing)    return REVIEW_LIST_MISSING_REASON;
  if (!s.updateDone)          return '① 가격 받기를 먼저 완료해주세요';
  if (s.pendingDropCount > 0) return `하락 ${s.pendingDropCount}건의 ✓/✗ 확인 후 반영 가능합니다`;
  return undefined;
};

/** Naver 반영 버튼 비활성 조건 — DailyWorkflowBar 전용(위 naverBlockReason 참고). */
export const isNaverDisabled = (s: WorkflowState): boolean =>
  s.naverSyncing || s.priceUpdating || !!s.reviewListMissing ||
  s.selectedCount === 0 || !s.updateDone || s.pendingDropCount > 0;

/**
 * 오늘의 작업 워크플로우(① 수정가 조회 → ② 하락 확인 → ③ Naver 반영)의
 * 파생 상태와 액션을 한 곳에서 제공한다. 활성화 게이트(`naverBlockReason`/`naverDisabled`)는
 * "오늘 전체 조회 완료" 여부를 본다. (옛 카드 관리 PriceFilterBar 의 선택 카드 기준 게이트
 * `naverSelectionBlockReason` · `isNaverSelectionDisabled` 는 쓰는 곳이 없어 함께 지웠다 — 감사 card-controltower#713.)
 */
export function useWorkflow() {
  const { jobs, selection, filters, cards } = useCardPage();
  const edit = useCardEdit();

  const state: WorkflowState = {
    updateDone:       jobs.updateDone,
    pendingDropCount: edit.pendingConfirmIds.size,
    naverDone:        jobs.naverDone,
    priceUpdating:    jobs.priceUpdating,
    naverSyncing:     jobs.naverSyncing,
    classifying:      jobs.classifying,
    selectedCount:    selection.selectedIds.length,
    totalCount:       cards.cards.length,
    reviewListMissing: jobs.reviewListMissing === true,
  };

  /** 하락 확인 화면으로 이동(필터를 DROP 으로 + 최상단 스크롤). */
  const goToReview = useCallback(() => {
    filters.setQuickFilter('DROP');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filters]);

  const startUpdate = useCallback(
    () => jobs.startUpdatePrices(selection.selectedIds),
    [jobs, selection.selectedIds],
  );

  // ③ 스토어 반영은 DailyWorkflowBar 가 전체 카드로 직접 시작한다 — 선택 기준 startNaver 는 쓰는 곳이 없어 지웠다(감사 card-controltower#752).
  return {
    state,
    goToReview,
    startUpdate,
    naverBlockReason: naverBlockReason(state),
    naverDisabled:    isNaverDisabled(state),
  };
}